import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, Typography, Box, TableSortLabel, TextField, InputAdornment, Button, FormControlLabel, Checkbox, Snackbar, Alert, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { VariantInterface } from '../../types';
import EditVariantDialog from './EditVariantDialog';
import SpecialPriceDialog from './SpecialPriceDialog';
import ImportOptionsDialog from '../ImportOptionsDialog';

interface VariantsTableProps {
  variants: VariantInterface[];
  onAddVariant: () => void;
  onVariantClick?: (variant: VariantInterface) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (newPage: number) => void;
  hideActions?: boolean;
  onRefresh?: () => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (column: string) => void;
  onSearch?: (query: string) => void;
  onResetSearch?: () => void;
  initialSearchQuery?: string;
  onlyActive?: boolean;
  setOnlyActive?: (val: boolean) => void;
  onlyAvailable?: boolean;
  setOnlyAvailable?: (val: boolean) => void;
  onExport?: () => void;
  onImport?: (data: any[]) => Promise<void>;
  onDownloadTemplate?: () => void;
  loading: boolean;
}

function VariantsTable({
  variants,
  onAddVariant,
  onVariantClick,
  page,
  pageSize,
  total,
  onPageChange,
  hideActions,
  onRefresh,
  sortBy,
  sortOrder,
  onSortChange,
  onSearch,
  onResetSearch,
  initialSearchQuery = '',
  onlyActive,
  setOnlyActive,
  onlyAvailable,
  setOnlyAvailable,
  onExport,
  onImport,
  onDownloadTemplate,
  loading,
}: VariantsTableProps): React.JSX.Element {
  const [localInput, setLocalInput] = React.useState(initialSearchQuery);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [importOptionsOpen, setImportOptionsOpen] = React.useState(false);

  React.useEffect(() => {
    setLocalInput(initialSearchQuery);
  }, [initialSearchQuery]);
  const [editVariant, setEditVariant] = React.useState<VariantInterface | null>(null);
  const [specialPriceVariant, setSpecialPriceVariant] = React.useState<VariantInterface | null>(null);
  const [toastOpen, setToastOpen] = React.useState(false);

  const handleCopy = (name: string): void => {
    navigator.clipboard.writeText(name);
    setToastOpen(true);
  };

  const handleEdit = (variant: VariantInterface) => {
    setEditVariant(variant);
  };
  const handleSpecialPrice = (variant: VariantInterface) => {
    setSpecialPriceVariant(variant);
  };
  const handleCloseEdit = () => setEditVariant(null);
  const handleCloseSpecialPrice = () => setSpecialPriceVariant(null);

  const totalPages = Math.ceil(total / pageSize);
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Typography variant="h6">קורסים</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onExport && (
              <IconButton
                color="primary"
                onClick={onExport}
                title="ייצוא ל-CSV"
                sx={{
                  backgroundColor: 'grey.300',
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'grey.400' }
                }}
              >
                <DownloadIcon />
              </IconButton>
            )}
          {onImport && (
            <>
              <input
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const Papa = await import('papaparse');
                  Papa.parse(file, {
                    header: true,
                    skipEmptyLines: true,
                    complete: async (results) => {
                      if (results.data) {
                        await onImport(results.data);
                      }
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }
                  });
                }}
              />
              <IconButton
                color="primary"
                onClick={() => {
                  if (onDownloadTemplate) {
                    setImportOptionsOpen(true);
                  } else {
                    fileInputRef.current?.click();
                  }
                }}
                title="ייבוא מ-CSV"
                sx={{
                  backgroundColor: 'grey.300',
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'grey.400' }
                }}
              >
                <UploadFileIcon />
              </IconButton>
              <ImportOptionsDialog
                open={importOptionsOpen}
                onClose={() => setImportOptionsOpen(false)}
                onImportClick={() => fileInputRef.current?.click()}
                onDownloadTemplateClick={() => onDownloadTemplate?.()}
                title="אפשרויות ייבוא קורסים"
              />
            </>
          )}
          {!hideActions && (
            <IconButton
                color="primary"
                onClick={onAddVariant}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }}
              >
                <AddIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, px: 2, pb: 2, alignItems: 'center' }}>
          <TextField
            label="חיפוש קורס"
            variant="outlined"
            size="small"
            value={localInput}
            onChange={(e) => setLocalInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') onSearch?.(localInput);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          <Button variant="contained" onClick={() => onSearch?.(localInput)}>חיפוש</Button>
          {(localInput || initialSearchQuery) && (
            <IconButton onClick={() => { setLocalInput(''); onResetSearch?.(); }} title="נקה חיפוש">
              <ClearIcon />
            </IconButton>
          )}
          {setOnlyActive && (
            <FormControlLabel
              control={<Checkbox checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} size="small" />}
              label={<Typography variant="body2">רק פעילים</Typography>}
              sx={{ ml: 1 }}
              title="האם הקורס פעיל או לא (אם לא, אי אפשר לרכוש בכלל)"
            />
          )}
          {setOnlyAvailable && (
            <FormControlLabel
              control={<Checkbox checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)} size="small" />}
              label={<Typography variant="body2">רק זמינים בחנות</Typography>}
              sx={{ ml: 1 }}
              title="האם המוצר זמין לרכישה בחנות (אם לא, עדיין אפשר לרכוש דרך מחיר מיוחד ולינק לרכישה)"

            />
          )}
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'id'}
                  direction={sortBy === 'id' ? sortOrder : 'asc'}
                  onClick={() => onSortChange?.('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortOrder : 'asc'}
                  onClick={() => onSortChange?.('name')}
                >
                  שם
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'price'}
                  direction={sortBy === 'price' ? sortOrder : 'asc'}
                  onClick={() => onSortChange?.('price')}
                >
                  מחיר
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'quantity'}
                  direction={sortBy === 'quantity' ? sortOrder : 'asc'}
                  onClick={() => onSortChange?.('quantity')}
                >
                  כמות
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'available'}
                  direction={sortBy === 'available' ? sortOrder : 'asc'}
                  onClick={() => onSortChange?.('available')}
                >
                  מופיע בחנות
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'active'}
                  direction={sortBy === 'active' ? sortOrder : 'asc'}
                  onClick={() => onSortChange?.('active')}
                >
                  פעיל
                </TableSortLabel>
              </TableCell>
              <TableCell>פרטים</TableCell>
              {!hideActions && <TableCell align="right">פעולות</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={hideActions ? 7 : 8} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : variants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={hideActions ? 7 : 8} align="center" sx={{ py: 3 }}>
                  לא נמצאו קורסים
                </TableCell>
              </TableRow>
            ) : (
              variants.map((variant) => (
                <TableRow key={variant.id} sx={!variant.active ? { color: 'gray', opacity: 0.6 } : {}}>
                  <TableCell>{variant.id || 'N/A'}</TableCell>
                  <TableCell>
                    {onVariantClick ? (
                      <Box
                        component="span"
                        sx={{
                          cursor: 'pointer',
                          color: variant.active ? 'primary.main' : 'text.secondary',
                          textDecoration: 'underline'
                        }}
                        onClick={() => onVariantClick(variant)}
                      >
                        {variant.name}
                      </Box>
                    ) : (
                      <Box
                        component="span"
                        sx={{ color: variant.active ? 'inherit' : 'text.secondary' }}
                      >
                        {variant.name}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{variant.price || 'N/A'}</TableCell>
                  <TableCell>{variant.is_unlimited ? 'ללא הגבלה' : (variant.quantity || 'N/A')}</TableCell>
                  <TableCell>{variant.available ? 'כן' : 'לא'}</TableCell>
                  <TableCell>{variant.active ? 'כן' : 'לא'}</TableCell>
                  <TableCell>{variant.details}</TableCell>
                  {!hideActions && (
                    <TableCell align="right">
                      <Tooltip title="ערוך">
                        <IconButton onClick={() => handleEdit(variant)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="העתק שם">
                        <IconButton onClick={() => handleCopy(variant.name)}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="מחיר מיוחד">
                        <IconButton onClick={() => handleSpecialPrice(variant)}>
                          <AttachMoneyIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {/* Pagination UI */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Typography variant="body2">
            מציג {from}-{to} מתוך {total} פריטים
          </Typography>
          <Box>
            <IconButton onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
              {'<'}
            </IconButton>
            <Typography variant="body2" component="span" sx={{ mx: 1 }}>
              עמוד {page} מתוך {totalPages}
            </Typography>
            <IconButton onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
              {'>'}
            </IconButton>
          </Box>
        </Box>
      </TableContainer>
      <EditVariantDialog
        open={!!editVariant}
        variant={editVariant}
        onClose={handleCloseEdit}
        onVariantUpdated={() => onRefresh?.()}
      />
      <SpecialPriceDialog
        open={!!specialPriceVariant}
        variant={specialPriceVariant}
        onClose={handleCloseSpecialPrice}
      />
      <Snackbar open={toastOpen} autoHideDuration={3000} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setToastOpen(false)} severity="success" sx={{ width: '100%' }}>
          השם הועתק בהצלחה!
        </Alert>
      </Snackbar>
    </>
  );
}

export default VariantsTable; 